import { useState, useEffect, useCallback, type ReactNode } from 'react';
import {
  AuthContext,
  type User,
  loginUser,
  registerUser,
  logoutUser,
  requestPasswordResetApi,
  resetPasswordApi,
  fetchCurrentUser,
} from '../lib/auth';
import { setAuthToken, getAuthToken } from '../lib/api';

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  const refreshUser = useCallback(async () => {
    const token = getAuthToken();
    if (!token) {
      setUser(null);
      setLoading(false);
      return;
    }
    try {
      const userData = await fetchCurrentUser();
      setUser(userData);
    } catch {
      setAuthToken(null);
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshUser();
  }, [refreshUser]);

  const login = async (email: string, password: string) => {
    const { token } = await loginUser(email, password);
    setAuthToken(token);
    const userData = await fetchCurrentUser();
    setUser(userData);
  };

  const register = async (email: string, password: string, display_name: string) => {
    const { token } = await registerUser(email, password, display_name);
    setAuthToken(token);
    const userData = await fetchCurrentUser();
    setUser(userData);
  };

  const logout = async () => {
    await logoutUser();
    setUser(null);
  };

  const requestPasswordReset = async (email: string) => {
    await requestPasswordResetApi(email);
  };

  const resetPassword = async (token: string, newPassword: string) => {
    await resetPasswordApi(token, newPassword);
  };

  return (
    <AuthContext.Provider
      value={{ user, loading, login, register, logout, requestPasswordReset, resetPassword, refreshUser }}
    >
      {children}
    </AuthContext.Provider>
  );
}
